import type { Metadata } from 'next';

import config from '@/config';
import { parseRobotsString } from './parse-robots-string.util';
import {
  buildListingPaginationHref,
  LISTING_PAGE_SUFFIX,
  parseListingPageParam,
} from './parse-listing-page-param';
import { shouldNoindexListingQuery } from './should-noindex-listing-query';
import type { SeoResolveData } from '../types';

const FALLBACK_METADATA: Metadata = {
  robots: { index: false, follow: false },
};

const NOINDEX_NOFOLLOW_ROBOTS: Metadata['robots'] = {
  index: false,
  follow: false,
};

const INDEX_FOLLOW_ROBOTS: Metadata['robots'] = {
  index: true,
  follow: true,
};

type MetadataSearchParams = Record<string, string | string[] | undefined>;

interface BuildMetadataFromResolveOptions {
  searchParams?: MetadataSearchParams;
  /** Pathname used for self-canonical on ?page=N (N≥2), e.g. /fanfics or /fanfics/fandoms/slug. */
  listingPathname?: string;
  /** @deprecated Pagination is indexable; kept for call-site compatibility. */
  noindexPagination?: boolean;
}

const toSingleString = (value: string | string[] | undefined): string => {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : '';
  }
  return typeof value === 'string' ? value : '';
};

const resolveListingPageForMetadata = (searchParams?: MetadataSearchParams): number => {
  if (!searchParams) return 1;
  const parsed = parseListingPageParam(searchParams.page);
  if (parsed.status === 'ok') return parsed.page;
  return 1;
};

/**
 * Builds Next.js Metadata from a /seo/resolve response. Falls back to noindex,
 * nofollow when metadata could not be resolved (page will still render via
 * the fanfics-custom/v2 fallback, but must not be indexed without confirmed SEO data).
 *
 * For ?page=N (N≥2): self-canonical, index/follow (unless search/sort noindex),
 * and «— страница N» suffix on title / description / OG / Twitter.
 */
export const buildMetadataFromResolve = (
  resolved: SeoResolveData | null,
  options?: BuildMetadataFromResolveOptions,
): Metadata => {
  if (!resolved) {
    return FALLBACK_METADATA;
  }

  const listingSearchParams = options?.searchParams;
  const listingPathname = options?.listingPathname;
  const pageNumber = resolveListingPageForMetadata(listingSearchParams);
  const pageSuffix = LISTING_PAGE_SUFFIX(pageNumber);
  const title = `${resolved.title}${pageSuffix}`;
  const description = resolved.description
    ? `${resolved.description}${pageSuffix}`
    : resolved.description;

  let canonicalPath = resolved.canonical;
  if (pageNumber >= 2 && listingPathname && listingSearchParams) {
    canonicalPath = buildListingPaginationHref(listingPathname, listingSearchParams, pageNumber);
  }

  const canonicalUrl = `${config.PUBLIC_URL}${canonicalPath}`;

  const metadata: Metadata = {
    title,
    description,
    metadataBase: new URL(config.PUBLIC_URL),
    alternates: {
      canonical: canonicalUrl,
    },
    robots: parseRobotsString(resolved.robots),
    openGraph: {
      title,
      description,
      url: canonicalUrl,
      siteName: 'Molwe',
      locale: 'ru_RU',
      type: 'website',
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
    },
  };

  if (listingSearchParams && shouldNoindexListingQuery(listingSearchParams)) {
    metadata.robots = NOINDEX_NOFOLLOW_ROBOTS;
  } else if (pageNumber >= 2) {
    metadata.robots = INDEX_FOLLOW_ROBOTS;
  }

  return metadata;
};

/**
 * Applies page≥2 SEO fields to static catalog metadata (e.g. /originals).
 */
export const applyListingPaginationMetadata = (
  metadata: Metadata,
  options: {
    listingPathname: string;
    searchParams: MetadataSearchParams;
  },
): Metadata => {
  const pageNumber = resolveListingPageForMetadata(options.searchParams);
  if (pageNumber < 2) {
    return metadata;
  }

  const pageSuffix = LISTING_PAGE_SUFFIX(pageNumber);
  const baseTitle = toSingleString(
    typeof metadata.title === 'string'
      ? metadata.title
      : typeof metadata.title === 'object' && metadata.title && 'absolute' in metadata.title
        ? (metadata.title.absolute as string | undefined)
        : undefined,
  );
  const baseDescription =
    typeof metadata.description === 'string' ? metadata.description : undefined;
  const title = baseTitle ? `${baseTitle}${pageSuffix}` : metadata.title;
  const description = baseDescription ? `${baseDescription}${pageSuffix}` : metadata.description;
  const canonicalPath = buildListingPaginationHref(
    options.listingPathname,
    options.searchParams,
    pageNumber,
  );
  const canonicalUrl = `${config.PUBLIC_URL}${canonicalPath}`;

  metadata.title = title;
  metadata.description = description;
  metadata.alternates = { ...(metadata.alternates ?? {}), canonical: canonicalUrl };
  if (metadata.openGraph) {
    metadata.openGraph = {
      ...metadata.openGraph,
      title: typeof title === 'string' ? title : metadata.openGraph.title,
      description: typeof description === 'string' ? description : metadata.openGraph.description,
      url: canonicalUrl,
    };
  }
  if (metadata.twitter) {
    metadata.twitter = {
      ...metadata.twitter,
      title: typeof title === 'string' ? title : metadata.twitter.title,
      description: typeof description === 'string' ? description : metadata.twitter.description,
    };
  }

  if (!shouldNoindexListingQuery(options.searchParams)) {
    metadata.robots = INDEX_FOLLOW_ROBOTS;
  }

  return metadata;
};
